import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { failedKycCountSelector, isIdpvAllowedInKycSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUserStore } from '@/hooks/use-user-store';
import { KycStackNavigationProp } from '@/navigation/kyc/types';
import { exitKycToLogin } from '@/navigation/navigation';
import { IdpvSource } from '@/navigation/types';
import { TFunction } from 'i18next';

import { initialKycScreens, kycRouteNames } from '../const';
import { useApplicantId } from './use-applicant-id';
import { useKycFieldsStore } from './use-kyc-fields';
import { useNavigateToErrorModal } from './use-navigate-to-error-modal';

type KycRetryControlParams = {
    hasIdScanFailed?: boolean;
    liveAccountEmail?: string;
};

type CtaLabel = 'logout' | 'done' | 'scan_id' | 'retry' | 'login';

type KycRetryControlResponse = {
    verificationFailureMessage: string;
    ctaLabel: CtaLabel;
    retryOrLogout: () => void;
};

const determineCtaLabelAndMessage = (
    idScanFailed: boolean,
    shouldGoIdpvFlow: boolean,
    t: TFunction<'kyc', undefined>,
    liveAccountEmail?: string
): {
    message: string;
    label: CtaLabel;
} => {
    if (liveAccountEmail) {
        return {
            label: 'login',
            message: t('verification_failed_duplicate_account_copy', { email: liveAccountEmail }),
        };
    }
    if (idScanFailed) {
        return { label: 'logout', message: t('verification_failed_copy_account_locked') };
    }
    if (shouldGoIdpvFlow) {
        return { label: 'scan_id', message: t('verification_failed_copy_idpv') };
    }
    return { label: 'retry', message: t('verification_failed_copy') };
};

/*
 * Manages KYC verification failure scenarios and determines appropriate actions.
 *
 * @param {KycRetryControlParams} params - The retry control parameters.
 * @param {boolean} params.hasIdScanFailed - Indicates if the ID scan has failed.
 * @returns {KycRetryControlResponse} - The data for KYC retry control.
 */
export const useKycRetryControl = ({
    hasIdScanFailed = false,
    liveAccountEmail,
}: KycRetryControlParams): KycRetryControlResponse => {
    const { t } = useTranslation('kyc');
    const navigation = useNavigation<KycStackNavigationProp<'KycVerificationFailed'>>();
    const actions = useUserStore(state => state.actions);

    const navigateToErrorModal = useNavigateToErrorModal();

    const globalSettings = useJurisdictionStore(store => store.jurisdictionSettings?.globalSettings?.featureFlags);
    const isIdpvEnabled = globalSettings?.idpv_flow_enabled.enabled ?? false;

    const { data: retryCount = 0 } = useAuthUserConfig({
        select: failedKycCountSelector,
        refetchOnMount: 'always',
    });

    // Fetch the personal flag indicating if IDPV is allowed for this user during KYC
    // This flag is used to determine whether to show IDPV or repeat the SSN9 step
    const { data: isIdpvAllowedInKyc = true } = useAuthUserConfig({
        select: isIdpvAllowedInKycSelector,
    });

    const setFieldValue = useKycFieldsStore(state => state.setFieldValue);

    const { data } = useApplicantId();

    const isRetryCountSufficient = retryCount === initialKycScreens.indexOf(kycRouteNames.SCAN_ID);
    const isApplicantIdValid = !!data?.applicant_id;

    // Determine if we should proceed with IDPV based on multiple conditions:
    // 1. IDPV is globally enabled
    // 2. The retry count is sufficient (user has failed the initial KYC steps)
    // 3. IDPV is allowed for this specific user during KYC (based on the new personal flag) -- This flag is from /auth/user/config endpoint
    const shouldProceedWithIdpv = isIdpvEnabled && isRetryCountSufficient && isIdpvAllowedInKyc;
    const isAccountLocked = hasIdScanFailed || retryCount > initialKycScreens.length - 1;

    const { message: verificationFailureMessage, label: ctaLabel } = determineCtaLabelAndMessage(
        isAccountLocked,
        shouldProceedWithIdpv,
        t,
        liveAccountEmail
    );

    const retryOrLogout = () => {
        if (liveAccountEmail || isAccountLocked) {
            actions.logout();
            exitKycToLogin(navigation);
        } else if (shouldProceedWithIdpv) {
            isApplicantIdValid ? navigation.navigate('KycScanId', { source: IdpvSource.KYC }) : navigateToErrorModal();
        } else {
            setFieldValue('ssn', '');
            navigation.navigate(kycRouteNames.SSN_RETRY);
        }
    };

    return {
        verificationFailureMessage,
        ctaLabel,
        retryOrLogout,
    };
};
